import { useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import HoneypotField from "@/components/HoneypotField";
import ContactSuccess from "@/components/ContactSuccess";
import ContactInfoPanel from "@/components/ContactInfoPanel";
import AppointmentForm from "@/components/AppointmentForm";
import PhoneInputField from "@/components/PhoneInputField";
import TurnstileWidget from "@/components/TurnstileWidget";
import { useDetectedCountry } from "@/hooks/useDetectedCountry";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useContactForm, ENQUIRY_TYPES } from "@/hooks/useContactForm";

const fieldLabelStyle = { color: "var(--gg-secondary-text)" };

const Contact = () => {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("enquiry");
  const detectedCountry = useDetectedCountry();
  const turnstileRef = useRef(null);

  const { fields, setters, validity, submission } = useContactForm(preselected);
  const { fullName, email, phone, company, enquiryType, message, consent, honeypot } = fields;
  const { setFullName, setEmail, setPhone, setCompany, setEnquiryType, setMessage, setConsent, setHoneypot, setTurnstileToken } = setters;
  const { emailValid, phoneValid } = validity;
  const { submitting, submitError, submitted, canSubmit, handleSubmit } = submission;

  if (submitted) {
    return <ContactSuccess />;
  }

  return (
    <div data-testid="page-contact">
      <SEO
        title="Begin a Conversation"
        description="For private-client enquiries, strategic partnerships, collaborations, media opportunities and conversations regarding the Gopi Gadhvi Group ecosystem, connect with our office."
        path="/contact"
      />
      <PageHero
        eyebrow="Begin a Conversation"
        heading="Some Conversations Create Entire Futures."
        intro="For private-client enquiries, strategic partnerships, appointment requests, collaborations and media opportunities, connect with our office below."
        minHeight="38vh"
      />

      <section className="relative page-shell pb-28 grid lg:grid-cols-[1fr_360px] gap-10">
        <Reveal>
          <div className="glass-panel rounded-[28px] p-6 sm:p-10 md:p-12">
            <Tabs defaultValue="enquiry" className="w-full">
              <TabsList className="neu-inset mb-8 grid grid-cols-2 h-12" data-testid="contact-tabs">
                <TabsTrigger value="enquiry" data-testid="contact-tab-enquiry">Send an Enquiry</TabsTrigger>
                <TabsTrigger value="appointment" data-testid="contact-tab-appointment">Book an Appointment</TabsTrigger>
              </TabsList>

              <TabsContent value="enquiry">
            <form onSubmit={handleSubmit} className="space-y-7" data-testid="contact-form">
              <HoneypotField value={honeypot} onChange={setHoneypot} />

              <div>
                <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>
                  Full Name
                </label>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  data-testid="contact-fullname-input"
                  className="neu-inset h-11 font-sans text-[16px]"
                  style={{ color: "var(--gg-ivory)" }}
                />
              </div>

              <div>
                <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="contact-email-input"
                  className="neu-inset h-11 font-sans text-[16px]"
                  style={{ color: "var(--gg-ivory)" }}
                />
                {email && !emailValid && (
                  <p className="text-small mt-2" style={{ color: "#E39B9B" }} data-testid="contact-email-error">
                    Please enter a valid email address.
                  </p>
                )}
              </div>

              <div>
                <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>
                  Company or Organisation <span style={{ color: "var(--gg-muted-text)" }}>(optional)</span>
                </label>
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Company or organisation"
                  data-testid="contact-company-input"
                  className="neu-inset h-11 font-sans text-[16px]"
                  style={{ color: "var(--gg-ivory)" }}
                />
              </div>

              <div>
                <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>
                  Enquiry Type
                </label>
                <Select value={enquiryType} onValueChange={setEnquiryType}>
                  <SelectTrigger data-testid="contact-enquiry-type-select" className="neu-inset h-11 font-sans text-[16px]" style={{ color: "var(--gg-ivory)" }}>
                    <SelectValue placeholder="Select an enquiry type" />
                  </SelectTrigger>
                  <SelectContent style={{ background: "var(--gg-midnight-black)", borderColor: "rgba(42,42,42,0.85)", color: "var(--gg-pearl)" }}>
                    {ENQUIRY_TYPES.map((opt) => (
                      <SelectItem key={opt} value={opt} data-testid={`contact-enquiry-option-${opt.toLowerCase().replace(/\s+/g, "-")}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>
                  Phone Number <span style={{ color: "var(--gg-muted-text)" }}>(optional)</span>
                </label>
                <PhoneInputField
                  value={phone}
                  defaultCountry={detectedCountry}
                  onValueChange={setPhone}
                  testId="contact-phone-input"
                />
                {phone && !phoneValid && (
                  <p className="text-small mt-2" style={{ color: "#E39B9B" }} data-testid="contact-phone-error">
                    Please enter a valid phone number.
                  </p>
                )}
              </div>

              <div>
                <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>
                  Message <span style={{ color: "var(--gg-muted-text)" }}>(Optional)</span>
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us a little about your enquiry..."
                  rows={5}
                  data-testid="contact-message-input"
                  className="neu-inset font-sans text-[16px] resize-none"
                  style={{ color: "var(--gg-ivory)" }}
                />
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  data-testid="contact-consent-checkbox"
                  className="mt-1"
                  style={{ borderColor: "var(--gg-fine-border)" }}
                />
                <label className="text-body">
                  I agree to be contacted regarding my enquiry and acknowledge the{" "}
                  <a href="/privacy" className="link-quiet">Privacy Policy</a>.
                </label>
              </div>

              <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} testId="contact-turnstile" />

              {submitError && (
                <p className="text-small" style={{ color: "#E39B9B" }} data-testid="contact-submit-error">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="contact-submit-btn"
                className="btn-solid-pearl w-full uppercase tracking-wide text-[0.95rem]"
              >
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Submit
              </button>

              {!canSubmit && !submitting && (
                <p className="text-center text-small" data-testid="contact-verify-hint">
                  Complete all required fields and accept the consent statement to enable submission.
                </p>
              )}
            </form>
              </TabsContent>

              <TabsContent value="appointment">
                <AppointmentForm />
              </TabsContent>
            </Tabs>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <ContactInfoPanel />
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
